// FIXED BusinessUserPayment.js
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import React, { useState } from "react";
import axios from "../utils/axiosHandler";

const BusinessUserPayment = ({ amount, email, onPaymentComplete, config }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [savePaymentMethod, setSavePaymentMethod] = useState(true);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError("");

    let paymentIntentId = null;

    try {
      console.log("Starting business verification payment process");

      // Create payment intent for business verification
      const { data } = await axios.post("/api/business-verification-charge", {
        amount: amount,
        email: email,
      });

      paymentIntentId = data.paymentIntentId;
      console.log("Payment intent created:", paymentIntentId);

      // Create payment method first
      const { error: paymentMethodError, paymentMethod } =
        await stripe.createPaymentMethod({
          type: "card",
          card: elements.getElement(CardElement),
          billing_details: {
            email: email,
          },
        });

      if (paymentMethodError) {
        console.error("Payment method creation error:", paymentMethodError);
        setError(paymentMethodError.message);
        throw new Error(paymentMethodError.message);
      }

      console.log("Payment method created:", paymentMethod.id);

      // Confirm payment with the created payment method
      const { error: confirmError, paymentIntent } =
        await stripe.confirmCardPayment(data.clientSecret, {
          payment_method: paymentMethod.id,
        });

      if (confirmError) {
        console.error("Payment confirmation error:", confirmError);
        setError(confirmError.message);
        throw new Error(confirmError.message);
      }

      if (paymentIntent.status === "succeeded") {
        console.log("Payment succeeded:", paymentIntent.id);

        // Call the completion handler with payment method info
        onPaymentComplete(
          paymentIntent.id,
          paymentMethod.id, // Pass the payment method ID
          savePaymentMethod
        );
      } else {
        throw new Error(`Payment failed with status: ${paymentIntent.status}`);
      }
    } catch (err) {
      console.error("Payment error:", err);

      // If we have a payment intent ID and the error occurred after creation,
      // attempt to refund it automatically
      if (paymentIntentId) {
        console.log("Attempting to refund failed payment:", paymentIntentId);
        try {
          await axios.post("/api/refund-verification-payment", {
            paymentIntentId: paymentIntentId,
            reason: "Payment processing failed",
          });
          console.log("Auto-refund successful for failed payment");
        } catch (refundError) {
          console.error("Auto-refund failed:", refundError);
          // Don't show refund error to user, just log it
        }
      }

      // Better error handling for user display
      let errorMessage = "Payment failed";
      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      setProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold mb-4">
          Business Account Verification
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          A verification charge of ${amount.toFixed(2)} will be processed and
          immediately refunded after account verification.
        </p>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 rounded-md p-3 mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Card Information
            </label>
            <div className="border rounded-md p-3">
              <CardElement
                options={{
                  style: {
                    base: {
                      fontSize: "16px",
                      color: "#424770",
                      "::placeholder": {
                        color: "#aab7c4",
                      },
                    },
                  },
                }}
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={savePaymentMethod}
                onChange={(e) => setSavePaymentMethod(e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">
                Save this payment method for future orders (This will allow
                automatic charging for business orders)
              </span>
            </label>
          </div>

          <div className="flex space-x-3">
            <button
              type="button"
              onClick={() => onPaymentComplete(null, null, false)}
              className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-md hover:bg-gray-300"
              disabled={processing}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!stripe || processing}
              className="flex-1 bg-green-400 text-white py-2 px-4 rounded-md disabled:opacity-50"
            >
              {processing ? "Processing..." : `Pay $${amount.toFixed(2)}`}
            </button>
          </div>
        </form>

        <div className="mt-4 text-xs text-gray-500">
          <p>
            • Your card will be charged ${amount.toFixed(2)} for verification
          </p>
          <p>• This amount will be automatically refunded after verification</p>
          <p>
            • If you choose to save the payment method, it will be used for
            future business orders
          </p>
        </div>
      </div>
    </div>
  );
};

export default BusinessUserPayment;
